import type { IViewColorConfig, IViewConfig } from "lola-framework-ui-test";

/** Heading / title color from the design (not the brand green). */
export const TITLE_COLOR = "#051510";

/** Fallback when the partner theme omits `colors.inactived`. */
export const DISABLED_BUTTON_BG = "#979797";

export const errorInitialState = {
  title: null,
  subTitle: null,
  message: null,
  btnText: null,
  isOut: false,
};

export const themeInitialState = {
  font: {
    h1: {
      fontWeight: "400",
      min: "1.75rem",
      max: "2rem",
      lineHeight: "1",
    },
    highlight: {
      fontWeight: "700",
      min: "1.75rem",
      max: "2rem",
      lineHeight: "0.95",
    },
    h2: {
      fontWeight: "600",
      min: "1.25rem",
      max: "1.5rem",
      lineHeight: "1",
    },
    bodycopy: {
      fontWeight: "500",
      min: "1rem",
      max: "1.25rem",
      lineHeight: "1.25rem",
    },
    secondaryCta: {
      fontWeight: "500",
      min: "0.74rem",
      max: "1rem",
      lineHeight: "1",
    },
    footerText: {
      fontWeight: "500",
      min: "0.85rem",
      max: "0.85rem",
      lineHeight: "1",
    },
    mainButtonText: {
      fontWeight: "600",
      min: "1.15rem",
      max: "1.25rem",
      lineHeight: "2rem",
    },
    step: {
      fontWeight: "600",
      min: "0.5rem",
      max: "0.875rem",
      lineHeight: "1",
    },
    fontfamily: "Helvetica",
    fontcdn: "",
  },
  colors: {
    primaryGradient: "#111111",
    secondaryGradient: "#475268",
    secondaryColor: "#252525",
    whiteColor: "#FFFFFF",
    inactived: "#979797",
    errorColor: "#dc251c",
    partnerHighlights: "#ffffff",
    gradientDeg: "180deg",
    primaryGradientPoint: "0%",
    secundaryGradientPoint: "100%",
    primaryMesh: "linear-gradient(180deg, #111111 0%, #475268 100%)",
    gradient: "linear-gradient(180deg, #111111 0%, #475268 100%)",
    lightness: "dark",
  },
};

const baseView: Omit<IViewColorConfig, "viewConfig"> = {
  background: "#fff",
  iconColors: ["#000", "#000"],
  backgroundIcon: "#fff",
  title: "#000",
  subtitile: "#000",
  bodyCopy: "#000",
  footerColor: "#000",
  backgroundBtn: "#000",
  textColorBtn: "#fff",
  stepsColors: "#000",
  dropzoneColors: ["#000", "#000"],
  stepsLabelColor: "#000",
};

export const themeFormattedInitialState: IViewConfig = {
  primaryMeshGradientView: { ...baseView, viewConfig: "primaryMeshGradientView" },
  specialView: { ...baseView, viewConfig: "specialView" },
  dataView: { ...baseView, viewConfig: "dataView" },
  whiteView: { ...baseView, viewConfig: "whiteView" },
  errorView: { ...baseView, viewConfig: "errorView" },
};

export const APP_ROUTES = {
  LOADING_PAGE: "/",
  WELCOME_PAGE: "/welcome-page",
  LANDING_PAGE: "/landing-page",
  HOME_PAGE: "/home",
  /** Operation history hub (same screen as `HOME_PAGE`). */
  TRANSACTIONS_PAGE: "/home",
  TRANSFER_PAGE: "/transfer-page",
  ADD_CARD_PAGE: "/add-card-page",
  ADD_CARD_ADDRESS_PAGE: "/add-card-address-page",
  ADD_CARD_PROCESSING_PAGE: "/add-card-processing-page",
  CIP_PAGE: "/cip",
  /** Contact phone. Insert after CIP (or confirm when CIP is skipped) and before the irreversible step. */
  PHONE_PAGE: "/phone",
  CASH_DEPOSIT_PAGE: "/cash-deposit-page",
  SENDING_MONEY_PAGE: "/sending-money-page",
  HOW_TO_DEPOSIT_CASH_PAGE: "/how-to-deposit-cash-page",
  STORES_MAP_PAGE: "/stores-map",
  RECEIPT_SUMMARY_PAGE: "/receipt-page/:operationId",
  LEGAL_PAGE: "/legal",
  TERM_DETAIL_PAGE: "/term-detail",
  SMS_TERMS_PAGE: "/sms-terms",
  ERROR_PAGE: "/error-page",
  SESSION_EXPIRED_PAGE: "/session-expired",
  CANCELLATION_IN_PROGRESS_PAGE: "/cancellation-in-progress",
  CANCELLATION_FAILED_PAGE: "/cancellation-failed",
  SERVICE_UNAVAILABLE_PAGE: "/service-unavailable",
  MAINTENANCE_PAGE: "/maintenance",
  SUPPORT_PAGE: "/support",
};

export const US_STATES = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "PR", name: "Puerto Rico" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "VI", name: "Virgin Islands" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
] as const;
